<template>
    <VJoyPanel :id="anchorsPageProfile.certificationsSection" class="profile-certifications" padding="large">
        <template #panel-title>
            <h2>{{ t('profile.section.certifications.title') }}</h2>
        </template>
        <template #panel-title-action>
            <LazyCertificationTranslation v-if="isTranslationActivated && certificationSectionState.length > 0" />
            <LazyCertificationEditionTopActions v-if="isEditionEnabled && certificationSectionState.length > 0" />
        </template>

        <template #panel-body>
            <template v-if="certificationSectionState.length > 0">
                <LazyCertificationListDraggable v-if="isEditionEnabled" />
                <LazyCertificationList v-else />
                <ShowMoreByItemCount
                    test-id="toggle-show-all-certifications"
                    :total="certificationsCount"
                    :limit="CERTIFICATIONS_TO_DISPLAY_BY_DEFAULT"
                    :label="t('profile.section.certifications.show.all.label.v2', {count: certificationsCount})"
                    :locked="{
                        allowHref: true,
                        label: t('profile.section.certifications.private', [profile?.firstname]),
                        componentName: 'profile-certifications-section',
                        targetId: anchorsPageProfile.certificationsSection,
                    }"
                    @expand="showCertifications(true)"
                    @collapse="showCertifications(false)"
                />
            </template>
            <EmptyContentSection
                v-else-if="isEditionEnabled"
                icon="file-check"
                :title="t('profile.section.certifications.empty.content.title')"
                :description="t('profile.section.certifications.empty.content.description')"
            >
                <template #action>
                    <ClientOnly>
                        <VJoyButton icon="add" size="small" variant="secondary" @click="openAddCertification">
                            {{ t('profile.section.certifications.add') }}
                        </VJoyButton>
                        <template #fallback>
                            <VJoyFormFieldSkeleton size="small" width="250px" />
                        </template>
                    </ClientOnly>
                </template>
            </EmptyContentSection>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {storeToRefs} from 'pinia';
    import {
        LazyCertificationEditionTopActions,
        LazyCertificationList,
        LazyCertificationListDraggable,
        LazyCertificationTranslation,
    } from '#components';
    import {computed, useProfilePageViewMode, useTranslation, ref, watch} from '#imports';
    import {VJoyPanel, VJoyButton, VJoyFormFieldSkeleton} from '@maltjoy/core-vue';
    import ShowMoreByItemCount from '~/components/_common/show-more/ShowMoreByItemCount.vue';
    import EmptyContentSection from '~/components/section/EmptyContentSection.vue';
    import {anchorsPageProfile} from '~/config/anchors';
    import {eventBus} from '~/config/events';
    import {EditionOpenedEntryPoint} from '~/config/TrackingEvents';
    import {useCertificationSectionState} from '~/features/certifications/composables/useCertificationSectionState';
    import {useProfileStore} from '~/store/profileStore';
    import {useTranslationStore} from '~/store/translation/translationStore';
    import {certificationMapper} from '~/types/infra/CertificationMapper';
    import type {CertificationRequest} from '~/types/infra/CertificationRequest';

    const CERTIFICATIONS_TO_DISPLAY_BY_DEFAULT = 2;

    // Composables
    const {t} = useTranslation();
    const {isEditionEnabled} = useProfilePageViewMode();

    // State
    const certificationSectionState = useCertificationSectionState();

    // Stores
    const {certifications, profile} = storeToRefs(useProfileStore());
    const {isTranslationActivated} = storeToRefs(useTranslationStore());
    const isShowingAllCertifications = ref<boolean>(false);

    const certificationsCount = computed<number>(() => certifications.value?.items.length || 0);
    const certificationRequests = computed<CertificationRequest[]>(
        () => certifications.value?.items.map((certification, index) => certificationMapper.toRequest(certification, index)) || [],
    );
    const firstFewCertificationsRequests = computed<CertificationRequest[]>(() =>
        certificationRequests.value.slice(0, CERTIFICATIONS_TO_DISPLAY_BY_DEFAULT),
    );

    function initDraggableList() {
        certificationSectionState.value = isShowingAllCertifications.value ? certificationRequests.value : firstFewCertificationsRequests.value;
    }

    function showCertifications(showAll: boolean): void {
        isShowingAllCertifications.value = showAll;
        initDraggableList();
    }

    initDraggableList();

    watch(
        () => certifications.value,
        () => {
            initDraggableList();
        },
    );

    function openAddCertification() {
        eventBus.emit('certifications:edit-mode', {certificationId: undefined, entryPoint: EditionOpenedEntryPoint.PROFILE_PAGE});
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .profile-certifications {
        scroll-margin-top: calc(var(--header-menu-height) + var(--joy-core-spacing-2));
    }

    .profile-certifications__list {
        margin: 0;
        list-style-type: none;
        padding-left: 0;
    }
</style>
